import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Datastore, OrderByDirection } from '@freelancer/datastore';
import {
  ProfileViewUser,
  Showcase,
  ShowcaseCollection,
} from '@freelancer/datastore/collections';
import { RedirectToPhp } from '@freelancer/redirect-to-php';
import { ButtonColor } from '@freelancer/ui/button';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { trackById } from '@freelancer/ui/helpers';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import { TextAlign, TextSize } from '@freelancer/ui/text';
import { ShowcaseStatusApi } from 'api-typings/users/users';
import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'app-user-profile-showcase',
  template: `
    <fl-card [edgeToEdge]="true">
      <fl-card-header-title i18n="Portfolio heading">
        Portfolio Items
      </fl-card-header-title>

      <fl-card-header-right>
        <ng-container *ngIf="isLoggedIn$ | flAsync; else isLoggedOut">
          <ng-container *ngIf="isViewerProfileOwner$ | flAsync">
            <fl-button
              i18n="Portfolio manage button"
              flTrackingLabel="UserProfileManagePortfolio"
              [color]="ButtonColor.SECONDARY"
              (click)="handleManagePortfolioClicked()"
            >
              Manage
            </fl-button>
          </ng-container>
        </ng-container>

        <ng-template #isLoggedOut>
          <app-user-profile-logged-out-link
            flTrackingLabel="LoggedOutUserProfilesPortfolioViewFullProfile"
            [username]="(user$ | flAsync)?.username"
          ></app-user-profile-logged-out-link>
        </ng-template>
      </fl-card-header-right>

      <fl-bit class="PortfolioCardContent">
        <ng-container *ngIf="showcases$ | flAsync as showcases">
          <fl-bit class="UserPortfolio-empty" *ngIf="showcases.length === 0">
            <fl-picture
              alt="No portfolios yet"
              i18n-alt="Alt text for portfolio empty state"
              class="UserPortfolio-empty-image"
              [display]="PictureDisplay.BLOCK"
              [flMarginBottom]="Margin.SMALL"
              [src]="'portfolio/illustration.svg'"
              [lazyLoad]="true"
            ></fl-picture>
            <fl-text
              class="UserPortfolio-empty-label"
              i18n="Copy if user has no portfolio items yet."
              [size]="TextSize.SMALL"
              [textAlign]="TextAlign.CENTER"
              [flMarginBottom]="Margin.MID"
            >
              No portfolio items have been added yet
            </fl-text>
          </fl-bit>
          <fl-bit *ngIf="user$ | flAsync as user">
            <fl-grid [vAlign]="VerticalAlignment.VERTICAL_STRETCH">
              <fl-col
                *ngFor="let showcase of showcases; let last = last"
                [col]="12"
                [colTablet]="6"
                [colDesktopSmall]="4"
                [flMarginBottom]="Margin.MID"
              >
                <app-discover-portfolio-item-card
                  *ngIf="
                    !(
                      last &&
                      ((showLoadMore$ | flAsync) || (isBusy$ | flAsync))
                    )
                  "
                  [showcase]="showcase"
                  [username]="user.username"
                ></app-discover-portfolio-item-card>
              </fl-col>
            </fl-grid>
          </fl-bit>
          <fl-bit class="UserPortfolio-footer">
            <ng-container *ngIf="isLoggedIn$ | flAsync; else isLoggedOutAction">
              <fl-button
                *ngIf="(showLoadMore$ | flAsync) || (isBusy$ | flAsync)"
                i18n="Load more button label"
                [flTrackingLabel]="'LoadMore'"
                [color]="ButtonColor.DEFAULT"
                [busy]="isBusy$ | flAsync"
                (click)="loadMore()"
              >
                Load more
              </fl-button>
            </ng-container>
            <ng-template #isLoggedOutAction>
              <app-user-profile-logged-out-link
                flTrackingLabel="LoggedOutUserProfilesPortfolioViewFullProfile"
                [username]="(user$ | flAsync)?.username"
              ></app-user-profile-logged-out-link>
            </ng-template>
          </fl-bit>
        </ng-container>
      </fl-bit>
    </fl-card>
  `,
  styleUrls: ['./user-profile-showcase.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileShowcaseComponent implements OnInit {
  ButtonColor = ButtonColor;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  ShowcaseStatusApi = ShowcaseStatusApi;
  TextAlign = TextAlign;
  TextSize = TextSize;
  VerticalAlignment = VerticalAlignment;
  trackById = trackById;

  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;
  @Input() profileUserId$: Rx.Observable<number>;
  @Input() user$: Rx.Observable<ProfileViewUser>;

  PAGE_SIZE = 6;

  private currentLimit: number = this.PAGE_SIZE + 1;
  private limit$ = new Rx.BehaviorSubject<number>(this.PAGE_SIZE + 1);

  showcases$: Rx.Observable<readonly Showcase[]>;
  isBusy$: Rx.Observable<boolean>;
  showLoadMore$: Rx.Observable<boolean>;

  constructor(
    private datastore: Datastore,
    private redirectToPhp: RedirectToPhp,
  ) {}

  ngOnInit() {
    const showcaseCollection = this.datastore.collection<ShowcaseCollection>(
      'showcase',
      query =>
        query
          .search({ status: ShowcaseStatusApi.ACTIVE })
          .where('userId', '==', this.profileUserId$)
          .limit(this.limit$.asObservable())
          .orderBy('id', OrderByDirection.DESC),
    );
    this.showcases$ = showcaseCollection.valueChanges();
    this.isBusy$ = showcaseCollection.status$.pipe(
      map(status => !status.ready),
    );

    this.showLoadMore$ = Rx.combineLatest([this.limit$, this.showcases$]).pipe(
      map(([limit, showcases]) => limit <= showcases.length),
    );
  }

  loadMore(): void {
    this.currentLimit += this.PAGE_SIZE;
    this.limit$.next(this.currentLimit);
  }

  handleManagePortfolioClicked(): void {
    // TODO: Remove this when the webapp editable portfolios is functional.
    this.user$
      .pipe(take(1))
      .toPromise()
      .then(user => {
        this.redirectToPhp.doRedirect(`/u/${user.username}?page=portfolio`);
      });
  }
}
